import { api } from './client'
import type {
  AddDealershipRequest,
  ApiTeam,
  ApiTeamMemberInvite,
  ConnectBodyshopRequest,
  ConnectedBodyshopResponse,
  CreateTeamRequest,
  DealershipGroupResponse,
  DisconnectBodyshopRequest,
  InviteMemberRequest,
  TeamMemberSummary,
  UpdateTeamMemberDepartmentRequest,
  UpdateTeamMemberRoleRequest,
  UpdateTeamRequest,
  UpdateTeamSettingsRequest,
} from './types'

export const teamsApi = {
  // GET /teams — flat ApiTeam[] of teams the current user belongs to.
  list: () =>
    api.get<ApiTeam[]>('/teams'),

  create: (data: CreateTeamRequest) =>
    api.post<ApiTeam>('/teams', data),

  get: (teamId: number) =>
    api.get<ApiTeam>(`/teams/${teamId}`),

  update: (teamId: number, data: UpdateTeamRequest) =>
    api.patch<ApiTeam>(`/teams/${teamId}`, data),

  // PUT /teams/:teamId/settings — owns address fields + the fees/rates metadata
  // block. The `name` field lives on PATCH /teams/:teamId, not here.
  updateSettings: (teamId: number, data: UpdateTeamSettingsRequest) =>
    api.put<ApiTeam>(`/teams/${teamId}/settings`, data),

  invite: (teamId: number, data: InviteMemberRequest) =>
    api.post<ApiTeamMemberInvite>(`/teams/${teamId}/invites`, data),

  listInvites: (teamId: number) =>
    api.get<ApiTeamMemberInvite[]>(`/teams/${teamId}/invites`),

  revokeInvite: (teamId: number, uuid: string) =>
    api.delete<void>(`/teams/${teamId}/invites/${uuid}`),

  resendInvite: (teamId: number, uuid: string) =>
    api.post<ApiTeamMemberInvite>(`/teams/${teamId}/invites/${uuid}/resend`, {}),

  // GET returns a flat list of dealership Teams. The POST counterpart still
  // returns the join row (DealershipGroupResponse) — the BE chose this
  // asymmetry: GET callers only need the team, POST callers want the link
  // metadata (createdAt, isActive, etc).
  listDealerships: (groupId: number) =>
    api.get<ApiTeam[]>(`/teams/${groupId}/group/dealerships`),

  addDealership: (groupId: number, data: AddDealershipRequest) =>
    api.post<DealershipGroupResponse>(`/teams/${groupId}/group/dealerships`, data),

  deleteDealership: (groupId: number, dealershipId: number) =>
    api.delete<void>(`/teams/${groupId}/group/dealerships/${dealershipId}`),

  // POST /teams/:dealershipId/bodyshop — links a body shop by name. If a
  // shop with `bodyshopName` already exists, BE reuses it; otherwise a new
  // one is created and `generalManagerEmail` becomes required. The typed
  // return lets callers read `bodyshop.id` for follow-up cache invalidation.
  connectBodyshop: (dealershipId: number, data: ConnectBodyshopRequest) =>
    api.post<{
      id: number
      dealership: ApiTeam
      bodyshop: ApiTeam
      isActive: boolean
      createdAt: string
      updatedAt: string
    }>(`/teams/${dealershipId}/bodyshop`, data),

  getConnectedBodyshop: (dealershipId: number) =>
    api.get<ConnectedBodyshopResponse>(`/teams/${dealershipId}/bodyshop`),

  disconnectBodyshop: (dealershipId: number, data: DisconnectBodyshopRequest) =>
    api.delete<void>(`/teams/${dealershipId}/bodyshop`, data),

  listMembers: (teamId: number) =>
    api.get<TeamMemberSummary[]>(`/teams/${teamId}/members`),

  removeMember: (teamId: number, userId: number) =>
    api.delete<void>(`/teams/${teamId}/members/${userId}`),

  updateMemberRole: (teamId: number, userId: number, data: UpdateTeamMemberRoleRequest) =>
    api.patch<TeamMemberSummary>(`/teams/${teamId}/members/${userId}`, data),

  /** Sales desk only — the endpoint validates against 'new' | 'used'. */
  updateMemberDepartment: (
    teamId: number,
    userId: number,
    data: UpdateTeamMemberDepartmentRequest,
  ) =>
    api.patch<TeamMemberSummary>(`/teams/${teamId}/members/${userId}/department`, data),

  // Bodyshop-side counterpart to listDealerships: the dealerships routing
  // estimates to this shop. Returns ApiTeam[] (not DealershipGroupResponse[],
  // since there is no group join on this side).
  listConnectedDealerships: (bodyshopId: number) =>
    api.get<ApiTeam[]>(`/teams/${bodyshopId}/bodyshop/dealerships`),
}
